import { useMemo, useState, useEffect } from 'react'
import { useCritterStore, DEFAULT_CRITTER_TYPES } from '../store/critterStore'
import useSettingsStore from '../store/settingsStore'
import ChatBubble from './ChatBubble'
import AnimatedSprite from './AnimatedSprite'
//import {SleepIndicator } from './CritterEffects'
import '../styles/Critter.css'

// API base URL for custom critter images
const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001'

function Critter({ critter }) {
  const {
    username,
    critterType,
    position,
    mouthOpen,
    isFeeding,
    isPetting,
    isDancing,
    isDoingTrick,
    isHugging,
    isBonking,
    chatMessage,
    spawning,
    despawning,
    isSleeping
  } = critter

  // Subscribe to individual settings to ensure proper re-renders
  const staticMode = useSettingsStore((state) => state.settings.staticMode)
  const critterScale = useSettingsStore((state) => state.settings.critterScale)
  const allowChatBubbles = useSettingsStore((state) => state.settings.allowChatBubbles)
  const showUsernames = useSettingsStore((state) => state.settings.showUsernames)
  const enableAnimations = useSettingsStore((state) => state.settings.enableAnimations)
  const critterTypes = useCritterStore((state) => state.critterTypes)
  const broadcasterLogin = useCritterStore((state) => state.broadcasterLogin)
  const typeConfig = critterTypes[critterType] || DEFAULT_CRITTER_TYPES.cat || Object.values(critterTypes)[0]

  // Animate mouth open/close when talking or feeding
  const [mouthFrame, setMouthFrame] = useState(false)

  useEffect(() => {
    if (mouthOpen || isFeeding || chatMessage) {
      // Animate mouth at ~4 frames per second for talking/eating effect
      const interval = setInterval(() => {
        setMouthFrame((prev) => !prev)
      }, 150)

      return () => clearInterval(interval)
    } else {
      setMouthFrame(false)
    }
  }, [mouthOpen, isFeeding, chatMessage])

  // Use animated mouth frame when talking/feeding, otherwise use static mouth state
  const showMouthOpen = (mouthOpen || isFeeding || chatMessage) ? mouthFrame : false
  const rawImageSrc = showMouthOpen ? typeConfig.mouthOpen : typeConfig.mouthClosed
  // Prepend API_BASE for uploaded images (start with /uploads/), keep as-is for local assets
  const imageSrc = rawImageSrc?.startsWith('/uploads/') ? `${API_BASE}${rawImageSrc}` : rawImageSrc

  const className = useMemo(() => {
    const classes = ['critter']
    if (spawning) classes.push('spawning')
    if (despawning) classes.push('despawning')
    if (isSleeping) classes.push('sleeping')
    if (mouthOpen || chatMessage) classes.push('mouth-open')
    if (isFeeding) classes.push('being-fed')
    if (isPetting) classes.push('being-petted')
    if (isDancing) classes.push('dancing')
    if (isDoingTrick) classes.push('doing-trick')
    if (isHugging) classes.push('hugging')
    if (isBonking) classes.push('being-bonked')
    return classes.join(' ')
  }, [spawning, despawning, isSleeping, mouthOpen, isFeeding, isPetting, isDancing, isDoingTrick, isHugging, isBonking, chatMessage])

  // Determine if sprite should be flipped based on facing direction
  const shouldFlip = critter.facingLeft

  // Calculate scale transform
  const scaleTransform = critterScale !== 1.0 ? `scale(${critterScale})` : ''

  return (
    <div
      className={className}
      style={{
        left: `${position.x}%`,
        bottom: `${position.y}%`,
        transform: scaleTransform,
        transformOrigin: 'bottom center'
      }}
    >
      {chatMessage && allowChatBubbles && (
        <ChatBubble username={username} message={chatMessage} />
      )}

      <div className="sprite-container">
        {/* Always render static image as fallback */}
        <img
          className={`critter-sprite fallback-sprite ${critter.isWalking && !enableAnimations && !isSleeping ? 'walking' : ''} ${(enableAnimations || staticMode || isSleeping) ? 'no-breathing' : ''}`}
          src={imageSrc}
          alt={`${username}'s ${typeConfig.name}`}
          draggable={false}
          style={{
            transform: shouldFlip ? 'scaleX(-1)' : 'scaleX(1)'
          }}
        />
        {/* Animated sprite overlays static image when loaded (disabled when sleeping) */}
        {enableAnimations && !isSleeping && (
          <AnimatedSprite
            critterType={critterType}
            isWalking={critter.isWalking}
            isFeeding={isFeeding}
            isPetting={isPetting}
            facingLeft={shouldFlip}
            className="critter-sprite animated-overlay"
            broadcasterLogin={broadcasterLogin}
          />
        )}
      </div>

      {showUsernames && <div className="critter-name">{username}</div>}

      {isSleeping && <SleepIndicator />}
      {isPetting && <Hearts />}
      {isFeeding && <FoodParticles />}
      {isDancing && <MusicNotes />}
      {isDoingTrick && <Sparkles />}
      {isHugging && <Hearts />}
      {isBonking && <Stars />}
    </div>
  )
}

// Hearts 2D animation component
function Hearts() {
  const hearts = [0, 1, 2, 3, 4]

  return (
    <>
      {hearts.map((i) => (
        <div
          key={i}
          className="heart"
          style={{
            left: `${50 + (Math.random() - 0.5) * 80}%`,
            top: `${20 + Math.random() * 30}%`,
            animationDelay: `${i * 0.1}s`
          }}
        >
          ❤️
        </div>
      ))}
    </>
  )
}

// Food particles animation component
function FoodParticles() {
  const foods = ['🍖', '🍕', '🍔', '🌮', '🍣', '🍪']
  const particles = [0, 1, 2]

  return (
    <>
      {particles.map((i) => (
        <div
          key={i}
          className="food-particle"
          style={{
            left: `${50 + (Math.random() - 0.5) * 60}%`,
            top: '30%',
            animationDelay: `${i * 0.15}s`
          }}
        >
          {foods[Math.floor(Math.random() * foods.length)]}
        </div>
      ))}
    </>
  )
}

// Sleep indicator (Zzz) component
function SleepIndicator() {
  return (
    <div className="sleep-indicator">
      <span className="zzz z1">z</span>
      <span className="zzz z2">z</span>
      <span className="zzz z3">z</span>
    </div>
  )
}

// Music notes for dancing
function MusicNotes() {
  const notes = ['🎵', '🎶', '🎵', '🎶', '🎵']

  return (
    <>
      {notes.map((note, i) => (
        <div
          key={i}
          className="music-note"
          style={{
            left: `${30 + (i * 10)}%`,
            animationDelay: `${i * 0.3}s`
          }}
        >
          {note}
        </div>
      ))}
    </>
  )
}

// Sparkle effects for tricks
function Sparkles() {
  const sparkles = [0, 1, 2, 3, 4, 5]

  return (
    <>
      {sparkles.map((i) => (
        <div
          key={i}
          className="sparkle"
          style={{
            left: `${20 + Math.random() * 60}%`,
            top: `${10 + Math.random() * 60}%`,
            animationDelay: `${i * 0.15}s`
          }}
        >
          ✨
        </div>
      ))}
    </>
  )
}

// Star effects for bonking
function Stars() {
  const stars = ['⭐', '💫', '⭐', '💫', '⭐']

  return (
    <>
      {stars.map((star, i) => (
        <div
          key={i}
          className="bonk-star"
          style={{
            left: `${20 + (i * 15)}%`,
            top: `${5 + Math.random() * 20}%`,
            animationDelay: `${i * 0.1}s`
          }}
        >
          {star}
        </div>
      ))}
    </>
  )
}

export default Critter